import { cn } from '@/lib/utils';
import { toast } from '@/lib/toast';
import { Checkbox } from '@/components/ui/checkbox';
import { useUpdateNurseryOnly } from '@/hooks/useProducts';

interface NurseryOnlyToggleProps {
  /** The variant the flag belongs to. */
  shopifyVariantId: number | null;
  /** Whether the variant is currently sold only at the nursery. */
  value: boolean;
  /** Optional text label rendered beside the checkbox (e.g. on mobile cards). */
  label?: string;
  className?: string;
}

/**
 * Toggle for a variant's "nursery only" flag — whether it is sold only at the
 * nursery and not online. Self-contained: a change writes `variants.nursery_only`
 * locally and pushes the Shopify `custom_fields.nursery` metafield (1/0). The
 * one nursery-only editor, shared by every page that needs it.
 */
export function NurseryOnlyToggle({
  shopifyVariantId,
  value,
  label,
  className,
}: NurseryOnlyToggleProps) {
  const update = useUpdateNurseryOnly();

  const onToggle = (checked: boolean) => {
    if (shopifyVariantId == null) {
      toast.error('This product has no variant to flag');
      return;
    }
    update.mutate(
      { shopifyVariantId, nurseryOnly: checked },
      {
        onSuccess: () =>
          toast.success(
            checked
              ? 'Marked nursery-only in Shopify'
              : 'Marked available online in Shopify',
          ),
        onError: (err) =>
          toast.error(`Saved locally, but Shopify update failed: ${err.message}`),
      },
    );
  };

  const checkbox = (
    <Checkbox
      checked={value}
      onCheckedChange={(c) => onToggle(c === true)}
      disabled={update.isPending}
      aria-label="Nursery only"
    />
  );

  if (!label) {
    return <span className={className}>{checkbox}</span>;
  }
  return (
    <label className={cn('inline-flex cursor-pointer items-center gap-2', className)}>
      {checkbox}
      <span className="text-xs text-muted-foreground">{label}</span>
    </label>
  );
}
